import { PDFPage, rgb } from "pdf-lib";
import {
  ACKNOWLEDGEMENT_SECTION_ID,
  buildCompanySafetyPolicySections,
  formatCompanySignatureLine,
  stripPolicySignatureLines,
  type PolicySection,
} from "./companySafetyPolicyContent";
import type { CompanySafetyPolicySignature } from "./companySafetyPolicyApi";
import {
  createPdfDocument,
  drawHeading,
  drawTextBlock,
  downloadPdfBytes,
} from "./pdfText";

export interface CompanySafetyPolicyPdfOptions {
  sections?: PolicySection[];
  adminSignatureSection?: PolicySection;
  signature?: CompanySafetyPolicySignature | null;
  policyVersion?: number;
}

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 50;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const addPage = (pdfDoc: Awaited<ReturnType<typeof createPdfDocument>>["pdfDoc"]) => {
  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  return { page, y: PAGE_HEIGHT - MARGIN };
};

const formatSignatureDate = (isoDate: string) =>
  new Date(isoDate).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

export const generateCompanySafetyPolicyPdf = async (
  companyName: string,
  options?: CompanySafetyPolicyPdfOptions
): Promise<Uint8Array> => {
  const { pdfDoc, font, boldFont } = await createPdfDocument();
  const sections =
    options?.sections && options.sections.length > 0
      ? options.sections
      : buildCompanySafetyPolicySections(companyName);

  let { page, y } = addPage(pdfDoc);

  page.drawText("COMPANY SAFETY POLICY", {
    x: MARGIN,
    y,
    size: 18,
    font: boldFont,
    color: rgb(0.05, 0.2, 0.45),
  });
  y -= 28;

  page.drawText(companyName, {
    x: MARGIN,
    y,
    size: 14,
    font: boldFont,
    color: rgb(0.1, 0.1, 0.15),
  });
  y -= 22;

  y = drawTextBlock(
    "This policy is designed for employers adopting OnMyWay. Customize for your organization. Not legal advice.",
    {
      page,
      font,
      x: MARGIN,
      y,
      maxWidth: CONTENT_WIDTH,
      fontSize: 9,
      lineHeight: 12,
      color: rgb(0.4, 0.4, 0.45),
    }
  );
  y -= 16;

  const ensureSpace = (needed: number) => {
    if (y < MARGIN + needed) {
      const next = addPage(pdfDoc);
      page = next.page;
      y = next.y;
    }
  };

  sections.forEach((section) => {
    ensureSpace(40);
    y = drawHeading(page, boldFont, section.title, MARGIN, y, 13);
    ensureSpace(20);

    const sectionParagraphs =
      section.id === ACKNOWLEDGEMENT_SECTION_ID
        ? [
            ...stripPolicySignatureLines(section.paragraphs),
            formatCompanySignatureLine(options?.signature ?? null),
          ]
        : section.paragraphs;

    sectionParagraphs.forEach((paragraph, paragraphIndex) => {
      ensureSpace(60);
      const isSignedCompanyLine =
        section.id === ACKNOWLEDGEMENT_SECTION_ID &&
        paragraphIndex === sectionParagraphs.length - 1 &&
        Boolean(options?.signature);

      y = drawTextBlock(paragraph, {
        page,
        font: isSignedCompanyLine ? boldFont : font,
        x: MARGIN,
        y,
        maxWidth: CONTENT_WIDTH,
        fontSize: 10,
        lineHeight: 13,
        bottomMargin: MARGIN,
        color: isSignedCompanyLine ? rgb(0.05, 0.35, 0.2) : rgb(0.1, 0.1, 0.15),
      });
      y -= 8;
    });

    section.bullets?.forEach((bullet) => {
      const line = bullet.label
        ? `• ${bullet.label}: ${bullet.text}`
        : `• ${bullet.text}`;
      ensureSpace(40);
      y = drawTextBlock(line, {
        page,
        font,
        x: MARGIN + 8,
        y,
        maxWidth: CONTENT_WIDTH - 8,
        fontSize: 10,
        lineHeight: 13,
        bottomMargin: MARGIN,
      });
      y -= 4;
    });

    y -= 10;
  });

  const adminSection = options?.adminSignatureSection;
  if (options?.signature && adminSection) {
    const { signerName, signerTitle, signedAt, policyVersion } = options.signature;
    const signedNote =
      adminSection.paragraphs[1] ??
      "This attestation confirms the company administrator has reviewed and approved the published safety policy.";

    ensureSpace(100);
    y = drawHeading(page, boldFont, adminSection.title, MARGIN, y, 13);
    ensureSpace(60);

    const signerLine = signerTitle
      ? `Signed by: ${signerName}, ${signerTitle}`
      : `Signed by: ${signerName}`;

    y = drawTextBlock(signerLine, {
      page,
      font: boldFont,
      x: MARGIN,
      y,
      maxWidth: CONTENT_WIDTH,
      fontSize: 10,
      lineHeight: 14,
      bottomMargin: MARGIN,
    });
    y -= 6;

    y = drawTextBlock(`Date: ${formatSignatureDate(signedAt)}`, {
      page,
      font,
      x: MARGIN,
      y,
      maxWidth: CONTENT_WIDTH,
      fontSize: 10,
      lineHeight: 13,
      bottomMargin: MARGIN,
    });
    y -= 6;

    const version = options.policyVersion ?? policyVersion;
    y = drawTextBlock(`Policy version: ${version}`, {
      page,
      font,
      x: MARGIN,
      y,
      maxWidth: CONTENT_WIDTH,
      fontSize: 10,
      lineHeight: 13,
      bottomMargin: MARGIN,
    });
    y -= 8;

    y = drawTextBlock(signedNote, {
      page,
      font,
      x: MARGIN,
      y,
      maxWidth: CONTENT_WIDTH,
      fontSize: 9,
      lineHeight: 12,
      color: rgb(0.4, 0.4, 0.45),
      bottomMargin: MARGIN,
    });
  }

  const pages = pdfDoc.getPages();
  pages.forEach((p: PDFPage, index: number) => {
    p.drawText(`OnMyWay Safety Policy • Page ${index + 1} of ${pages.length}`, {
      x: MARGIN,
      y: 30,
      size: 8,
      font,
      color: rgb(0.5, 0.5, 0.5),
    });
  });

  return pdfDoc.save();
};

export const downloadCompanySafetyPolicyPdf = async (
  companyName: string,
  options?: CompanySafetyPolicyPdfOptions
) => {
  const bytes = await generateCompanySafetyPolicyPdf(companyName, options);
  const safeName = companyName.replace(/\s+/g, "_");
  const signedSuffix =
    options?.signature && options.signature.signerName
      ? "_Signed"
      : "";
  downloadPdfBytes(
    bytes,
    `${safeName}_Company_Safety_Policy${signedSuffix}.pdf`
  );
};
